import React from 'react';
import NProgress, { set } from 'nprogress';
import { useRouter } from 'next/router';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNotify } from './useNotify';
import {
  Address,
  Benefit,
  Company,
  CompanyStatusEnum,
  DocumentModel,
  User,
} from '../models';
import { roles } from '../static';
import { CREATE_USER, UPDATE_BENEFIT, UPDATE_USER } from '../graphql/mutation';
import { reducer as userReducer } from '../components/shared/Forms/GeneralUserFormFields';
import { reducer as addressReducer } from '../components/shared/Forms/AddressForm';
import { reducer as companyReducer } from '../components/shared/Forms/CompanyFormFields';
import {
  validateAddressForm,
  validateCompanyForm,
  validateUserForm,
} from '../lib/formValidations';
import { GET_BENEFIT } from '../graphql/queries';

interface CompanyFormProps {
  companyInfo?: User;
}

export const useCreateAndUpdateCompany = ({
  companyInfo,
}: CompanyFormProps) => {
  const router = useRouter();
  const notify = useNotify();
  // General states
  const [disabled, setDisabled] = React.useState(false);
  const [companyReadOnly, setCompanyReadOnly] = React.useState(false);
  const [addressReadOnly, setAddressReadOnly] = React.useState(false);
  const [benefit, setBenefit] = React.useState<Partial<Benefit> | null>(null);
  // Forms states
  const [documents, setDocuments] = React.useState<
    Array<Partial<DocumentModel>>
  >(
    (companyInfo?.company as Company)?.logo?.src
      ? [{ src: (companyInfo?.company as Company)?.logo?.src }]
      : []
  );
  const [userState, userDispatch] = React.useReducer(
    userReducer,
    companyInfo ?? {}
  );
  const [companyState, companyDispatch] = React.useReducer(
    companyReducer,
    companyInfo ? { ...(companyInfo.company as Company) } : {}
  );
  const [addressState, addressDispatch] = React.useReducer(
    addressReducer,
    companyInfo ? { ...(companyInfo.company as Company).addresses[0] } : {}
  );

  // mutations
  const [createCompanyMutation] = useMutation<{
    createUser: User;
  }>(CREATE_USER);

  const [updateCompanyMutation] = useMutation<{
    updateUser: User;
  }>(UPDATE_USER);

  const [updateBenefitMutation] = useMutation<{
    updateBenefit: {
      record: Benefit;
    };
  }>(UPDATE_BENEFIT);

  // Functions
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      const companyError = validateCompanyForm({
        ...companyState,
      });
      if (companyError) return notify(companyError, 'error');

      const userError = validateUserForm({
        user: { ...userState },
        update: !!companyInfo?._id,
        requiredDni: false,
      });

      if (userError) return notify(userError, 'error');

      // Override address values if they were filled by the user
      const addressToSend: Partial<Address> = {
        country: '',
        state: '',
        municipality: '',
        address1: '',
        address2: '',
        zip: '',
        ...addressState,
      };

      // validate address
      const addressError = validateAddressForm({ ...addressToSend });
      if (addressError) return notify(addressError, 'error');

      // build object
      const companyData = {
        ...userState,
        photo: documents[0]?.src as string,
        userType: roles.company,
        dniType: companyState.dniType ?? 'J',
        dni: companyState.dni,
        company: {
          ...companyState,
          dniType: companyState.dniType ?? 'J',
          status: companyInfo ? companyState.status : 'pending',
          logo: { src: (documents[0]?.src as string) ?? null },
        } as Company,
      };
      //  create or update user
      if (companyInfo) {
        await updateCompany({
          ...companyData,
          photo: {
            src: documents[0]?.src as string,
          },
          company: {
            ...companyData.company,
            addresses: [
              {
                ...addressToSend,
                userId: companyInfo._id,
                primary: true,
                __typename: undefined,
              } as Address,
            ],
          },
        } as Partial<User>);
        await updateBenefit(benefit);
      } else {
        await createCompany(companyData, addressToSend);
      }
    } catch (error) {
      // error duplicate email
      if (error.message.includes('dup key: { email')) {
        return notify('El correo ya se encuentra registrado', 'error');
      }
      // error duplicate dni
      if (error.message.includes('dup key: { dni')) {
        return notify('El DNI ya se encuentra registrado', 'error');
      }

      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createCompany = async (
    companyData: Partial<User>,
    addressToSend: Partial<Address>
  ) => {
    delete (companyData?.company as Company)?._id;
    delete (companyData?.company as Company)?.active;
    delete (companyData?.company as Company)?.shopCart;
    delete (companyData?.company as Company)?.addresses;
    delete (companyData?.company as Company)?.companyDetails;

    const { data } = await createCompanyMutation({
      variables: {
        data: {
          ...companyData,
          benefit: {
            title: benefit.title,
            value: benefit.value,
          },
          company: {
            ...(companyData.company as Company),
            __typename: undefined,
          },
          addresses: [
            {
              ...addressToSend,
              primary: true,
              __typename: undefined,
              _id: undefined,
            },
          ],
          companyId: companyState?._id ?? undefined,
          __typename: undefined,
        },
      },
    });
    if (data?.createUser?._id) {
      notify('Compañia creado con éxito', 'success');
      router.push('/app/companies');
    } else {
      notify('Ha ocurrido un error al crear el aliado', 'error');
    }
  };

  const updateCompany = async (companyData: Partial<User>) => {
    delete (companyData?.company as Company)?.shopCart;
    delete companyData.firstLogin;
    delete companyData.discountCoupons;
    delete companyData.currentSubscription;
    delete (companyData?.company as Company)?.companyDetails;

    const { data } = await updateCompanyMutation({
      variables: {
        data: {
          ...companyData,
          _id: companyInfo._id,
          ...(companyData.company
            ? {
                company: {
                  ...(companyData.company as Company),
                  __typename: undefined,
                },
              }
            : {}),
          client: undefined,
          __typename: undefined,
        },
      },
    });
    if (data?.updateUser?._id) {
      notify('Compañia actualizada con éxito', 'success');
      // router.push('/app/companies');
    } else {
      notify('Ha ocurrido un error al actualizar el aliado', 'error');
    }
  };

  const updateBenefit = async (benefitData: Partial<Benefit>) => {
    try {
      const { data } = await updateBenefitMutation({
        variables: {
          filter: {
            _id: benefitData._id,
          },
          record: {
            title: benefitData.title,
            value: benefitData.value,
            __typename: undefined,
          },
        },
      });
      if (!data?.updateBenefit?.record?._id) {
        notify('Ha ocurrido un error al actualizar el beneficio', 'error');
      } else {
        router.push('/app/companies');
      }
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const changeCompanyActive = async () => {
    const companyData = {
      active: !companyInfo.active,
    };
    await updateCompany(companyData);
  };

  const changeCompanyStatus = async (status: CompanyStatusEnum) => {
    const data = {
      company: {
        _id: companyState._id,
        status,
        addresses: [
          {
            ...addressState,
            userId: companyInfo._id,
            primary: true,
            __typename: undefined,
          } as Address,
        ],
      },
    } as Partial<User>;

    await updateCompany(data);
  };

  const [getBenefit] = useLazyQuery<{
    benefit: Benefit;
  }>(GET_BENEFIT, { fetchPolicy: 'no-cache' });

  const getBenefitInfo = async () => {
    const { data } = await getBenefit({
      variables: { filter: { company: (companyInfo.company as Company)._id } },
    });
    setBenefit(data?.benefit);
  };

  React.useEffect(() => {
    if (companyInfo) {
      getBenefitInfo();
    }
  }, []);

  return {
    benefit,
    setBenefit,
    disabled,
    setDisabled,
    companyReadOnly,
    setCompanyReadOnly,
    addressReadOnly,
    setAddressReadOnly,
    documents,
    setDocuments,
    userState,
    userDispatch,
    companyState,
    companyDispatch,
    addressState,
    addressDispatch,
    // Functions
    onSubmit,
    changeCompanyActive,
    changeCompanyStatus,
  };
};
